<template lang="pug">
v-dialog(lazy v-model="visible" persistent width='640px')
  v-card
    title-bar(dark) {{ title }}
    v-tabs.tabs(v-bind:class="{'disabled': uploading}" v-show="!value" @change="resetImageProperties" v-model='selectedTab' slider-color='primary' centered)
      v-tab
        span From my computer
      v-tab
        span From the web
    v-tabs-items.px-3.tabs-items(v-model="selectedTab")
      v-tab-item(v-bind:class="{'hidden': value}" :value="0")
        v-card-text
          file-dropzone.file-dropzone(
            @loadRawFile="onImageFileLoaded"
            @readFile="onImageFileRead"
            :allowedFileExtensions="allowedImageFileExtensions"
            :file="imageFile"
            @removeFile="resetImageProperties"
            :maxSizeInMB="maxSizeInMB"
          )
            template(slot="title")
              span Drag your image file here
          v-text-field.mx-4(label="Image hyperlink" v-model="imageHyperlink")
          image-preview(
            :windowOffset=625
            :imageUrl="imagePreview"
            :width.sync="width"
            :height.sync="height"
          )
      v-tab-item(v-bind:class="{'no-transition': value}" :value="1")
        v-card-text
          v-text-field.mx-4(label="URL" v-model="imageUrl")
          v-text-field.mx-4(label="Image hyperlink" v-model="imageHyperlink")
          image-preview(
            :windowOffset=510
            :imageUrl="imageUrl"
            :width.sync="width"
            :height.sync="height"
          )
    v-card-actions
      v-spacer
      v-btn(flat :disabled="uploading" @click="$emit('close')") Cancel
      v-btn.primary(@click="submitImage" :disabled="isSubmitDisabled") Upload
</template>

<script>
import { feathers } from '@/feathers-client';
import TitleBar from '@/Core/components/TitleBar';
import FileDropzone from '@/Core/components/FileDropzone';
import ImagePreview from './ImagePreview';
import ALLOWED_IMAGE_FILE_EXTENSIONS from './../../config/allowed-image-file-extensions';

export default {
  components: {
    TitleBar,
    FileDropzone,
    ImagePreview,
  },
  props: {
    title: {
      type: String,
      default: 'Image Properties',
    },
    value: {
      type: String,
      default: null,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    folderId: {
      type: Number,
      default: 0,
    },
    maxSizeInMB: {
      type: Number,
      default: 10,
    },
  },
  computed: {
    image() {
      const image = new Image();
      image.src = this.imageUrl;
      if (this.imageHyperlink) {
        image.title = this.imageHyperlink;
      }
      if (this.width) {
        image.width = this.width;
      }
      if (this.height) {
        image.height = this.height;
      }
      return image;
    },
    isSubmitDisabled() {
      return (!this.imageUrl && !this.imageFile) || this.uploading;
    },
  },
  watch: {
    visible() {
      this.resetImageProperties();
      this.selectedTab = 0;
      if (this.value) {
        this.selectedTab = 1;
        const element = document.createElement('div');
        element.innerHTML = this.value;
        const image = element.firstElementChild;
        const imageHasTitle = image.attributes.getNamedItem('title');
        const defaultTitle = '';
        this.imageUrl = image.src;
        // set title only if present in markup
        // the title works as reference to image hyperlink
        this.imageHyperlink = imageHasTitle ? image.title : defaultTitle;
        // set width only if present in markup
        // to avoid image distortion
        if (image.attributes.getNamedItem('width')) {
          this.width = image.width;
        }
        // set height only if present in markup
        // to avoid image distortion
        if (image.attributes.getNamedItem('height')) {
          this.height = image.height;
        }
        // if height not set in markup, calculate based on proportions
        if (this.width && !this.height) {
          this.height = this.computeHeight(image, this.width);
        }
        // if width not set in markup, calculate based on proportions
        if (this.height && !this.width) {
          this.width = this.computeWidth(image, this.height);
        }
        // if dimensions not set in markup, set to natural image dimensions
        if (!this.width && !this.height) {
          this.width = image.naturalWidth;
          this.height = image.naturalHeight;
        }
      }
    },
  },
  data() {
    return {
      maxImageSideLength: 9999,
      imageUrl: null,
      imageFile: null,
      imagePreview: null,
      width: null,
      height: null,
      selectedTab: 0,
      allowedImageFileExtensions: ALLOWED_IMAGE_FILE_EXTENSIONS,
      uploading: false,
      imageHyperlink: null,
      imgUrlDataRegex: /data:image\/(jpeg|jpg|png);base64,/,
      imgUrlSplitRegex: /^data:(.*?);base64,(.*)$/,
    };
  },
  methods: {
    submitImage() {
      if (this.imageUrl && this.imgUrlDataRegex.test(this.imageUrl)){
        const file = this.base64ImageToFile(this.imageUrl);
        this.uploadFile(file);
      } else if (this.imageFile) {
        this.uploadFile(this.imageFile);
      } else if (this.imageUrl) {
        this.insertValue();
      }
    },
    resetImageProperties() {
      this.imageUrl = '';
      this.imageFile = null;
      this.imagePreview = '';
      this.width = null;
      this.height = null;
      this.imageHyperlink = null;
    },
    insertValue() {
      this.$emit('insert', this.image);
    },
    onImageFileLoaded(file) {
      this.imageFile = file;
    },
    onImageFileRead(e) {
      const { result } = e.target;
      this.imagePreview = result;
    },
    async uploadFile(file) {
      // Create new form to upload
      const formData = new FormData();
      formData.set('attachments', file);
      formData.set('folder_id', this.folderId);
      try {
        this.uploading = true;
        const response = await feathers
          .service('files')
          .create(formData);

        this.uploading = false;
        this.imageUrl = response.path;
        this.insertValue();
      } catch (err) {
        this.uploading = false;
      }
    },
    base64ImageToFile(dataSrc) {
      const [, mime, base64Data] = dataSrc.match(this.imgUrlSplitRegex);
      const binaryData = atob(base64Data);
      const dataArray = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        dataArray[i] = binaryData.charCodeAt(i);
      }
      const name = `${new Date().valueOf()}.${mime.split('/')[1]}`
      const blob = new Blob([dataArray], { type: mime });
      const file = new File([blob], name, { type: mime });
      return file;
    }
  },
};
</script>

<style lang="scss" scoped>
  .tabs{
    border-bottom: 2px solid var(--v-background-lighten4);
    &.disabled{
      pointer-events: none;
      opacity: 0.5;
    }
  }
  .tabs-items{
    .no-transition{
      transition: none;
    }
    .hidden{
      opacity: 0;
    }
  }
  .file-dropzone{
    height: 180px;
  }
</style>
